import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { isNativeMobile } from '@/lib/mobile-mode';
import {
  APP_STORE_URL,
  ITUNES_LOOKUP_URL,
  UPDATE_DISMISSED_KEY,
  parseStoreVersion,
  shouldShowUpdatePrompt,
} from '@/lib/app-update';

// useAppUpdate — surfaces an "update available" prompt when the App Store has a
// newer NATIVE build than the one running. OTA JS bundles apply themselves
// (Capgo, silent), so this deliberately only covers the store-build gap.
//
// Native-only: on web it no-ops (returns updateAvailable:false) — the browser
// always serves the latest bundle, and itunes.apple.com would be CSP-blocked
// there anyway. The check runs on mount and again whenever the app is resumed
// from the background, which is exactly when a user who just updated via the
// App Store would come back.

export interface AppUpdateState {
  updateAvailable: boolean;
  storeVersion: string | null;
  dismiss: () => void;
  openStore: () => void;
}

export function useAppUpdate(): AppUpdateState {
  const [storeVersion, setStoreVersion] = useState<string | null>(null);
  const [available, setAvailable] = useState(false);
  // Guard against overlapping checks (mount + a burst of resume events).
  const checking = useRef(false);

  const check = useCallback(async () => {
    if (!isNativeMobile() || checking.current) return;
    checking.current = true;
    try {
      const [info, res] = await Promise.all([
        App.getInfo(),
        fetch(ITUNES_LOOKUP_URL, { cache: 'no-store' }),
      ]);
      const store = parseStoreVersion(await res.json());
      if (!store) return;
      let dismissed: string | null = null;
      try {
        dismissed = localStorage.getItem(UPDATE_DISMISSED_KEY);
      } catch {
        // private-mode / storage disabled — treat as never-dismissed.
      }
      setStoreVersion(store);
      setAvailable(
        shouldShowUpdatePrompt({ current: info.version, store, dismissedVersion: dismissed }),
      );
    } catch {
      // Offline or the lookup failed — never block the app on an update check.
    } finally {
      checking.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isNativeMobile()) return;
    void check();
    let remove: (() => void) | undefined;
    App.addListener('resume', () => {
      void check();
    }).then((handle) => {
      remove = () => handle.remove();
    });
    return () => remove?.();
  }, [check]);

  const dismiss = useCallback(() => {
    if (storeVersion) {
      try {
        localStorage.setItem(UPDATE_DISMISSED_KEY, storeVersion);
      } catch {
        // ignore — worst case the prompt reappears next launch.
      }
    }
    setAvailable(false);
  }, [storeVersion]);

  const openStore = useCallback(() => {
    // Capacitor maps window.open(..., '_blank') to the system handler, so an
    // apps.apple.com link opens the App Store app rather than the WebView.
    window.open(APP_STORE_URL, '_blank');
  }, []);

  return { updateAvailable: available, storeVersion, dismiss, openStore };
}
